import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useAppContext } from '../../app/provider/useAppContext';
import ShieldIconCheckout from '../../assets/icons/basket/ShieldIconCheckout.svg';
import { appConfig } from '../../config/app-config';
import { ORDER_SUMMARY_CONSTANTS } from '../constants/orderSummaryConstants';

import Button from "./Button";

export interface OrderSummaryItem {
    price: number;
    quantity: number;
    currencyCode?: string;
    locale?: string;
}

export interface OrderSummaryProps {
    items: OrderSummaryItem[];
    eventKey?: string;
    onAction?: () => void;
    onCheckout?: () => void;
}

const OrderSummary = ({ items, eventKey: propEventKey, onAction, onCheckout }: OrderSummaryProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const [context] = useAppContext();
    const location = useLocation();

    // Extract: /:storeKey/:eventKey/...
    const pathParts = location.pathname.split('/').filter(Boolean);
    const storeKey = params.storeKey || (pathParts.length > 0 ? pathParts[0] : appConfig.defaults.storeKey);
    let eventKey = propEventKey || params.eventKey || (pathParts.length > 1 ? pathParts[1] : appConfig.defaults.eventKey);

    if (!eventKey || eventKey === 'undefined' || eventKey === 'null') {
        eventKey = appConfig.defaults.eventKey;
    }

    const total = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
    );

    // Prefer item-provided currency/locale (already mapped from context), then context, then session
    const selectedCurrency =
        (items[0] && items[0].currencyCode) ||
        context?.currency ||
        sessionStorage.getItem('currency') ||
        'USD';
    const selectedLocale =
        (items[0] && items[0].locale) ||
        context?.locale ||
        sessionStorage.getItem('locale') ||
        undefined;

    const handleCheckout = () => {
        if (onAction) onAction();

        if (onCheckout) {
            onCheckout();
            return;
        }

        // Check if user is logged in (same logic as cart page)
        const pingToken = sessionStorage.getItem('ping_access_token');
        const isLoggedIn = context?.loggedIn || !!pingToken;

        if (isLoggedIn) {
            navigate(`/${storeKey}/checkout`);
        } else {
            // Store the redirect path so user is redirected to checkout after login
            localStorage.setItem('postLoginRedirect', `/${storeKey}/checkout`);
            navigate(`/${storeKey}/${eventKey}/account`);
        }
    };

    return (
        <div className="font-noto">
            {/* Total row – "Total:" label + formatted amount + tax line */}
            <div className="flex justify-between items-start">
                {/* Total label: #002B66, weight 500, size 14, 37×24 */}
                <span className="text-[14px] font-[500] leading-[24px] text-[#002B66] w-[37px] h-[24px]">
                    {ORDER_SUMMARY_CONSTANTS.TOTAL}
                </span>
                {/* Amount + tax – right-aligned column, gap 0 to achieve exactly 123px total section height */}
                <div className="flex flex-col items-end">
                    <span className="text-[16px] font-[600] leading-[24px] text-[#002B66] text-right">
                        {new Intl.NumberFormat(selectedLocale, {
                            style: 'currency',
                            currency: selectedCurrency,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(total)}
                    </span>
                    {/* "+ tax applied at checkout" – #002B66@70%, weight 400, size 12 */}
                    <span className="text-[12px] font-[400] leading-[18px] text-[#002B66]/70">
                        {ORDER_SUMMARY_CONSTANTS.TAX_APPLIED}
                    </span>
                </div>
            </div>

            {/* Action buttons – 16px gap below the Total block */}
            <div className="flex gap-3 mt-[16px]">
                {/* View Full Basket – secondary button, text #101828 */}
                <Button
                    variant="secondary"
                    onClick={() => {
                        if (onAction) onAction();
                        navigate(`/${storeKey}/cart`);
                    }}
                    className="w-[159px] h-[40px] flex items-center justify-center"
                >
                    <span className="inline-block w-[98px] h-[18px] text-[12px] font-[700] leading-[18px] text-center text-[#101828]">
                        {ORDER_SUMMARY_CONSTANTS.VIEW_FULL_BASKET}
                    </span>
                </Button>

                {/* Checkout – primary button */}
                <Button
                    variant="primary"
                    onClick={handleCheckout}
                    className="w-[159px] h-[40px] flex items-center justify-center gap-[6px]"
                    disabled={items.length === 0}
                >
                    <span className="inline-block w-[57px] h-[18px] text-[12px] font-[700] leading-[18px]">
                        {ORDER_SUMMARY_CONSTANTS.CHECKOUT}
                    </span>
                    <img src={ShieldIconCheckout} alt="Secure" className="w-[20px] h-[20px]" />
                </Button>
            </div>
        </div>
    );
};

export default OrderSummary;
